'use client'

import { useState } from 'react'
import { Users } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@elmnts/ui/popover'
import { usePresenceRoster, type RosterEntry } from '@/hooks/presence/usePresenceRoster'
import { usePresence } from '@/providers/PresenceContext'
import { usePresenceTick } from '@/hooks/presence/usePresenceTick'
import { useCanaisDosUsuarios, useChatsDosUsuarios } from '@/hooks/navigation/useCanaisDosUsuarios'
import { OndeEstaNaEquipe, PresenceRow } from '@/components/Presence/PresenceRow'
import { IfHasPermission } from '@/components/guards'
import { USERS_PERMISSIONS } from '@/lib/permissions/users'
import { presenceOf } from '@/lib/presence/presenceTime'

const PEEK_CAP = 8
const RANK = { online: 0, afk: 1 } as const

/** Puro: presentes (não-offline), ordenados online→afk depois por nome, cortados em `cap`. */
export function buildPeek(roster: RosterEntry[], cap: number): { shown: RosterEntry[]; extra: number } {
  const present = roster
    .filter((u) => presenceOf(u) !== 'offline')
    .sort(
      (a, b) =>
        RANK[presenceOf(a) as 'online' | 'afk'] - RANK[presenceOf(b) as 'online' | 'afk'] ||
        a.name.localeCompare(b.name),
    )
  const shown = present.slice(0, cap)
  return { shown, extra: present.length - shown.length }
}

/**
 * Ícone na rail: badge = nº online; hover → peek; clique → abre o drawer.
 * Monta só com `users:presence:read` — o gate mora AQUI, não no `AppRail`. Os
 * hooks acima rodam sempre, e isso é seguro: `useSystemUsers` e
 * `usePresenceSnapshot` já são gateados por dentro, nenhum request sai sem grant.
 */
export function PresenceRailButton() {
  const { roster, counts, isError } = usePresenceRoster()
  const { openDrawer } = usePresence()
  const now = usePresenceTick()
  const canais = useCanaisDosUsuarios()
  const chats = useChatsDosUsuarios()
  const [open, setOpen] = useState(false)

  if (isError) return null

  const { shown, extra } = buildPeek(roster, PEEK_CAP)

  return (
    <IfHasPermission require={USERS_PERMISSIONS.PRESENCE_READ}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Presença da equipe"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onClick={() => {
              setOpen(false)
              openDrawer()
            }}
            className="relative flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Users className="size-5" />
            {counts.online > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-success)] px-1 text-[10px] font-semibold text-white">
                {counts.online}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="end"
          className="flex w-72 flex-col gap-0 p-0"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <div className="flex items-baseline justify-between gap-2 border-b px-3 py-2">
            <p className="text-sm font-semibold text-foreground">Equipe</p>
            <p className="text-xs text-muted-foreground">
              {counts.online} online · {counts.afk} {counts.afk === 1 ? 'ausente' : 'ausentes'}
            </p>
          </div>
          {shown.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">Ninguém online agora.</p>
          ) : (
            <ul className="flex flex-col gap-0.5 p-1.5">
              {shown.map((user) => {
                const onde = { canais: canais.get(user.db_id), chats: chats.get(user.db_id) }
                return (
                  <OndeEstaNaEquipe key={user.db_id} user={user} now={now} {...onde}>
                    <li className="flex min-w-0 items-center gap-3 rounded-md px-1.5 py-1">
                      <PresenceRow user={user} now={now} {...onde} />
                    </li>
                  </OndeEstaNaEquipe>
                )
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              openDrawer()
            }}
            className="border-t px-3 py-2 text-left text-xs font-medium text-primary transition-colors hover:bg-accent"
          >
            {extra > 0 ? `Ver equipe completa (+${extra})` : 'Ver equipe completa'}
          </button>
        </PopoverContent>
      </Popover>
    </IfHasPermission>
  )
}
